import type { ClientKind } from "@/domain/clients/list-types";
import { toolCallStore, type ChatCardStage } from "@/shared/ui/chat-card/tool-call-store";
import type { BriefMeeting } from "@/features/clients/lib/meeting-time";

export interface MeetingBriefSeed {
  clientId: string | null;
  clientQuery: string | null;
}

export interface MeetingBriefClient {
  kind: ClientKind;
  subjectId: string;
  label: string;
}

export type MeetingBriefPdf = "yes" | "no";

export interface MeetingBrief {
  toolCallId: string;
  seed: MeetingBriefSeed;
  client: MeetingBriefClient | null;
  pdf: MeetingBriefPdf | null;
  meeting: BriefMeeting | null;
}

export function openMeetingBrief(
  toolCallId: string,
  seed: MeetingBriefSeed,
  meeting: BriefMeeting | null = null,
): void {
  STORE.open({
    toolCallId,
    seed,
    client: null,
    pdf: null,
    meeting: meeting === null ? null : { ...meeting, toldAt: new Date().toISOString() },
  });
}

export function meetingBriefAnswered(toolCallId: string): void {
  STORE.answered(toolCallId);
}

export function hasMeetingBrief(toolCallId: string): boolean {
  return STORE.has(toolCallId);
}

export function useMeetingBrief(toolCallId: string): MeetingBrief | null {
  return STORE.useEntry(toolCallId);
}

export function useMeetingBriefAnswering(toolCallId: string): boolean {
  return STORE.useAnswering(toolCallId);
}

export function useMeetingBriefStages(): (toolCallId: string) => ChatCardStage | null {
  return STORE.useStages();
}

export function chooseMeetingBriefClient(toolCallId: string, client: MeetingBriefClient): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, client, pdf: null }));
}

export function answerMeetingBriefPdf(toolCallId: string, pdf: MeetingBriefPdf): void {
  STORE.update(toolCallId, (entry) => ({ ...entry, pdf }));
}

function narrowEntry(value: unknown): MeetingBrief | null {
  if (!isRecord(value) || typeof value.toolCallId !== "string") return null;
  const seed = isRecord(value.seed) ? value.seed : {};
  return {
    toolCallId: value.toolCallId,
    seed: { clientId: text(seed.clientId), clientQuery: text(seed.clientQuery) },
    client: client(value.client),
    pdf: value.pdf === "yes" || value.pdf === "no" ? value.pdf : null,
    meeting: meetingOf(value.meeting),
  };
}

function meetingOf(value: unknown): BriefMeeting | null {
  if (!isRecord(value)) return null;
  const meeting = { time: text(value.time), place: text(value.place) };
  if (meeting.time === null && meeting.place === null) return null;
  return { ...meeting, toldAt: text(value.toldAt) };
}

function client(value: unknown): MeetingBriefClient | null {
  if (!isRecord(value)) return null;
  const { kind, subjectId, label } = value;
  if (kind !== "individual" && kind !== "corporation") return null;
  if (typeof subjectId !== "string" || subjectId === "" || typeof label !== "string") return null;
  return { kind, subjectId, label };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

const STORE = toolCallStore<MeetingBrief>({
  key: "simon.clients.meetingBrief",
  narrow: narrowEntry,
  stage: (entry) => (entry.pdf === null ? "awaiting" : "done"),
});
